import type { FirebaseApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';

/** App Checkデバッグトークンのグローバルフラグへアクセスするための型 */
type DebugTokenGlobal = typeof self & { FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean | string };

/**
 * App Check（不正利用対策）を初期化する。
 *
 * Firebase AI Logic はAPIキーを持たない代わりに、エンドポイントの不正利用は
 * App Check で防ぐ。reCAPTCHA Enterprise（Google Cloud）のサイトキーは
 * 公開前提の値であり、クライアントに埋め込んで問題ない。
 *
 * サイトキー（VITE_APPCHECK_RECAPTCHA_SITE_KEY）が未設定の場合は初期化を
 * スキップする。キー未発行のローカル開発などでもアプリを起動できるようにするため。
 *
 * @param app Firebaseアプリ
 * @returns 初期化したAppCheckインスタンス。スキップ時は null。
 */
export function initializeAppCheckService(
  app: FirebaseApp
): ReturnType<typeof initializeAppCheck> | null {
  const siteKey = import.meta.env.VITE_APPCHECK_RECAPTCHA_SITE_KEY;

  if (!siteKey) {
    console.warn(
      'App Check のサイトキー（VITE_APPCHECK_RECAPTCHA_SITE_KEY）が未設定のため、App Check を初期化しません。'
    );
    return null;
  }

  // ローカル開発（localhost）では reCAPTCHA が検証できないため、デバッグトークンを
  // 有効化する。起動時にコンソールへ出力されるトークンを Firebase Console の
  // App Check に登録すると、ローカルからも App Check 必須のAPIを呼び出せる。
  if (import.meta.env.DEV) {
    (self as DebugTokenGlobal).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
  }

  return initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(siteKey),
    isTokenAutoRefreshEnabled: true,
  });
}
